import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { cn } from '@/lib/cn'
import { fetchGql } from '@/lib/graphql_client'
import { LegalEntitiesDocument, NaturalPersonsDocument } from '@/gql/graphql'
import { Text } from '@/components/ui/Text'

export interface PartyPickerProps {
  value?: string
  onChange: (partyId: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  excludePartyIds?: string[]
  /** When true, only natural persons are listed (legal entities are excluded). */
  personsOnly?: boolean
  /** Scopes both legalEntities and naturalPersons queries to the entity's owning tenant. */
  actingTenantId?: string
  /** Renders an error ring on the trigger (form validation). */
  invalid?: boolean
  /** Extra classes for the trigger (e.g. a white form background in form contexts). */
  className?: string
  /** Associates a describedby/error element for a11y. */
  'aria-describedby'?: string
  /** HTML id for the trigger, so an external `<label htmlFor>` points at it. */
  id?: string
  /** Id of an external label element; names the combobox instead of the placeholder. */
  ariaLabelledBy?: string
  /** When provided, a "＋ Add new person" action appears in the dropdown footer.
   *  Receives the current search text so callers can pre-fill a name field. */
  onCreatePerson?: (searchText: string) => void
}

export function PartyPicker({
  value,
  onChange,
  label,
  placeholder = 'Select a party...',
  disabled = false,
  excludePartyIds = [],
  personsOnly = false,
  actingTenantId,
  invalid = false,
  className,
  'aria-describedby': ariaDescribedBy,
  id,
  ariaLabelledBy,
  onCreatePerson,
}: PartyPickerProps) {
  const [searchText, setSearchText] = useState('')

  const { data: entitiesData } = useQuery({
    queryKey: ['legalEntities', 'picker', actingTenantId],
    queryFn: () => fetchGql(LegalEntitiesDocument, actingTenantId ? { actingTenantId } : {}),
    enabled: !personsOnly,
  })

  const { data: personsData } = useQuery({
    queryKey: ['naturalPersons', 'picker', actingTenantId],
    queryFn: () => fetchGql(NaturalPersonsDocument, actingTenantId ? { actingTenantId } : {}),
  })

  const options: ComboboxOption[] = useMemo(() => {
    const result: ComboboxOption[] = []
    const excludeSet = new Set(excludePartyIds)

    if (!personsOnly && entitiesData?.legalEntities) {
      for (const entity of entitiesData.legalEntities) {
        if (!excludeSet.has(entity.party_id)) {
          result.push({
            value: entity.party_id,
            label: entity.name,
            description: entity.entity_type ?? 'Legal Entity',
            group: 'Entities',
          })
        }
      }
    }

    if (personsData?.naturalPersons) {
      for (const person of personsData.naturalPersons) {
        if (!excludeSet.has(person.party_id)) {
          result.push({
            value: person.party_id,
            label: person.name,
            description: 'Person',
            group: 'People',
          })
        }
      }
    }

    return result
  }, [entitiesData, personsData, excludePartyIds, personsOnly])

  const createPersonFooter = onCreatePerson ? (
    <button
      type="button"
      onClick={() => onCreatePerson(searchText)}
      className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-primary hover:bg-primary-subtle/50 transition-colors"
    >
      <span aria-hidden>＋</span>
      Add new person
    </button>
  ) : undefined

  return (
    <div className="space-y-1.5" aria-describedby={ariaDescribedBy}>
      {label && (
        <Text variant="label-lg" as="label">{label}</Text>
      )}
      <Combobox
        id={id}
        ariaLabelledBy={ariaLabelledBy}
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        grouped
        clearable
        className={cn(className, invalid && 'border border-danger ring-1 ring-danger')}
        footer={createPersonFooter}
        onSearch={onCreatePerson ? setSearchText : undefined}
      />
    </div>
  )
}
